import { ChevronDownIcon, ChevronUpIcon } from '@digital-retail/design-system-icons';
import { withTheme } from '@digital-retail/falabella-ui-cross-components';
import PropTypes from 'prop-types';
import { useState } from 'react';
import icons from '../../../../../config/styles/icons';
import { noop } from '../../../../../utils/noop';
import { svgToDataUri } from '../../../../../utils/styles/svgMixins';
import Image from '../../../../ui/Image/Image';
import { useImageGalleryCarousel } from '../../hooks/useImageGalleryCarousel';
import { ImageGalleryCarouselV2Styles } from './style';

const ImageGalleryCarouselDesktop = ({
  carouselImageHeight,
  carouselImageWidth,
  activeImageIndex,
  activeIndicatorIndex,
  imagesPerSlide,
  setActiveImageIndex,
  setActiveIndicatorIndex,
  carouselImages,
  images,
  productName,
  brandName,
  theme,
}) => {
  const playIconStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 20,
    width: '30px',
    height: '30px',
    backgroundImage: `url('${svgToDataUri(icons.playIcon)}')`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    pointerEvents: 'none',
  };
  const [startIndex, setStartIndex] = useState(0);
  const themedStyles = ImageGalleryCarouselV2Styles(theme);

  const { processedCarouselItems, shouldShowNavigation, canScrollUp, canScrollDown, handleCarouselItemClick } =
    useImageGalleryCarousel({
      carouselImages,
      images,
      activeImageIndex,
      activeIndicatorIndex,
      imagesPerSlide,
      setActiveImageIndex,
      setActiveIndicatorIndex,
      productName,
      brandName,
      carouselImageWidth,
      carouselImageHeight,
      startIndex,
    });

  const handleScrollUp = () => {
    if (canScrollUp) {
      setStartIndex(Math.max(0, startIndex - 5));
    }
  };

  const handleScrollDown = () => {
    if (canScrollDown) {
      setStartIndex(startIndex + 5);
    }
  };

  const renderCarouselItem = (itemData) => {
    if (itemData.isVideo) {
      // Find the first image item to use as background
      const firstImageItem = processedCarouselItems.find((item) => !item.isVideo);

      if (firstImageItem) {
        return (
          <div className="image-gallery-carousel__item-video image-gallery-carousel__item-video--with-thumbnail">
            <Image
              id={firstImageItem.mediaId}
              imageUrl={firstImageItem.mediaUrl}
              width={itemData.width}
              height={itemData.height}
              alt={itemData.altText}
              lazyLoad
              inlineDimensions={false}
              carrouselImagePosition={itemData.index + 1}
            />
            <div style={playIconStyles} />
          </div>
        );
      }

      return (
        <div className="image-gallery-carousel__item-video">
          <div style={playIconStyles} />
        </div>
      );
    }
    return (
      <Image
        id={itemData.mediaId}
        imageUrl={itemData.mediaUrl}
        width={itemData.width}
        height={itemData.height}
        alt={itemData.altText}
        lazyLoad
        inlineDimensions={false}
        carrouselImagePosition={itemData.index + 1}
      />
    );
  };

  return (
    <div className="image-gallery-carousel image-gallery-carousel--desktop image-gallery-carousel--rebranded">
      {/* Navigation Arrow Up */}
      {shouldShowNavigation && canScrollUp && (
        <button
          type="button"
          className="image-gallery-carousel__nav-arrow image-gallery-carousel__nav-arrow--up"
          onClick={handleScrollUp}
          aria-label="Scroll up"
        >
          <ChevronUpIcon boxSize="16px" color="gray.600" />
        </button>
      )}

      <div className="image-gallery-carousel__container">
        {processedCarouselItems.map((itemData) => (
          <div
            className={`image-gallery-carousel__item ${
              itemData.isActive ? 'image-gallery-carousel__item--active' : ''
            } ${itemData.isVideo ? 'image-gallery-carousel__item--video' : ''}`}
            onClick={() => handleCarouselItemClick(itemData.index)}
            role="button"
            key={itemData.key}
            tabIndex="0"
            onKeyDown={noop}
            id={itemData.id}
          >
            {renderCarouselItem(itemData)}
          </div>
        ))}
      </div>

      {/* Navigation Arrow Down */}
      {shouldShowNavigation && canScrollDown && (
        <button
          type="button"
          className="image-gallery-carousel__nav-arrow image-gallery-carousel__nav-arrow--down"
          onClick={handleScrollDown}
          aria-label="Scroll down"
        >
          <ChevronDownIcon boxSize="16px" color="gray.600" />
        </button>
      )}

      <style jsx>{themedStyles}</style>
    </div>
  );
};

ImageGalleryCarouselDesktop.defaultProps = {
  images: [],
  carouselImages: [],
  theme: null,
};

ImageGalleryCarouselDesktop.propTypes = {
  carouselImageHeight: PropTypes.number.isRequired,
  carouselImageWidth: PropTypes.number.isRequired,
  carouselImages: PropTypes.array,
  activeImageIndex: PropTypes.number.isRequired,
  activeIndicatorIndex: PropTypes.number.isRequired,
  imagesPerSlide: PropTypes.number.isRequired,
  setActiveImageIndex: PropTypes.func.isRequired,
  setActiveIndicatorIndex: PropTypes.func.isRequired,
  images: PropTypes.array,
  productName: PropTypes.string.isRequired,
  brandName: PropTypes.string.isRequired,
  theme: PropTypes.object,
};

export { ImageGalleryCarouselDesktop };
export default withTheme(ImageGalleryCarouselDesktop);
